import React, { useState, useEffect } from 'react';
import { Search, Filter, RefreshCw, Printer, FileDown, MoreVertical, List, CreditCard, Upload, CheckCircle, AlertCircle as AlertCircleIcon, Eye } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchAdminTransactions,
  fetchCardActivity,
  selectTransactions,
  selectTransactionsTotal,
  selectTransactionsLoading,
  setPagination,
  selectTransactionsPagination,
  setActiveReport,
  selectActiveReport,
  uploadTransactionsCsv
} from '../../store/slices/transactionsSlice';
import type { AdminTransaction } from '../../services/api';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import TransactionDetailsModal from '../modals/TransactionDetailsModal';
import { getRunningBalance, getTransactionCid, downloadReceiptFile, printReceipt } from '../../utils/receiptGenerator';
import './ReportsPage.css';

interface TransactionActionDropdownProps {
  transaction: AdminTransaction;
  onViewDetails: (tx: AdminTransaction) => void;
  onDownloadReceipt: (tx: AdminTransaction) => void;
  onPrintReceipt: (tx: AdminTransaction) => void;
}

const TransactionActionDropdown: React.FC<TransactionActionDropdownProps> = ({
  transaction,
  onViewDetails,
  onDownloadReceipt,
  onPrintReceipt,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action: 'view' | 'download' | 'print' | 'flag') => {
    setIsOpen(false);
    if (action === 'view') {
      onViewDetails(transaction);
    } else if (action === 'download') {
      onDownloadReceipt(transaction);
    } else if (action === 'print') {
      onPrintReceipt(transaction);
    } else if (action === 'flag') {
      alert(`Transaction ${transaction.reference || transaction.id} has been flagged for audit review.`);
    }
  };

  return (
    <div className="transaction-action-dropdown">
      <button
        className="action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button
              className="dropdown-item"
              onClick={() => handleAction('view')}
            >
              <Eye size={14} style={{ marginRight: '8px' }} /> View Details
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('download')}
            >
              <FileDown size={14} style={{ marginRight: '8px' }} /> Download Receipt
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('print')}
            >
              <Printer size={14} style={{ marginRight: '8px' }} /> Print Receipt
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('flag')}
            >
              Flag Transaction
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const ReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const transactions = useAppSelector(selectTransactions);
  const total = useAppSelector(selectTransactionsTotal);
  const loading = useAppSelector(selectTransactionsLoading);
  const { page, limit } = useAppSelector(selectTransactionsPagination);
  const activeReport = useAppSelector(selectActiveReport);

  const [searchTerm, setSearchTerm] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState<{ success: number; skipped: number; errors: string[] } | null>(null);
  const [selectedTransactionForDetails, setSelectedTransactionForDetails] = useState<AdminTransaction | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleViewDetails = (tx: AdminTransaction) => {
    setSelectedTransactionForDetails(tx);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadReceipt = (tx: AdminTransaction) => {
    downloadReceiptFile(tx);
  };

  const handlePrintReceipt = (tx: AdminTransaction) => {
    printReceipt(tx);
  };

  useEffect(() => {
    if (activeReport === 'all') {
      dispatch(fetchAdminTransactions({ page, limit }));
    } else {
      dispatch(fetchCardActivity({ page, limit }));
    }
  }, [dispatch, page, limit, activeReport]);

  const handlePageChange = (newPage: number) => {
    dispatch(setPagination({ page: newPage }));
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setPagination({ limit: parseInt(e.target.value), page: 1 }));
  };

  const handleRefresh = () => {
    if (activeReport === 'all') {
      dispatch(fetchAdminTransactions({ page, limit }));
    } else {
      dispatch(fetchCardActivity({ page, limit }));
    }
  };

  const handleReportTypeChange = (type: 'all' | 'card-activity') => {
    dispatch(setActiveReport(type));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const resultAction = await dispatch(uploadTransactionsCsv(file));
      if (uploadTransactionsCsv.fulfilled.match(resultAction)) {
        setUploadResults(resultAction.payload);
        setIsUploadModalOpen(true);
        handleRefresh(); // Refresh the list
      } else {
        alert(resultAction.payload || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Local filtering for immediate feedback, though ideally server-side
  const filteredTransactions = transactions.filter(transaction => {
    const searchStr = searchTerm.toLowerCase();
    return (
      (transaction.reference || '').toLowerCase().includes(searchStr) ||
      (transaction.description || '').toLowerCase().includes(searchStr) ||
      (transaction.user?.company?.legalName || '').toLowerCase().includes(searchStr) ||
      (transaction.status || '').toLowerCase().includes(searchStr) ||
      (transaction.type || '').toLowerCase().includes(searchStr)
    );
  });

  const handleExportCSV = () => {
    if (filteredTransactions.length === 0) return;

    // Define headers
    const headers = ['Date', 'Company/User', 'CID', 'Type', 'Description', 'Amount', 'Running Balance', 'Reference', 'Status'];

    // Map data to CSV rows
    const rows = filteredTransactions.map(tx => [
      `"${new Date(tx.createdAt).toLocaleString()}"`,
      `"${tx.user?.company?.legalName || (tx.user ? `${tx.user.firstName} ${tx.user.lastName}` : 'N/A')}"`,
      `"${getTransactionCid(tx)}"`,
      `"${tx.type}"`,
      `"${tx.description || ''}"`,
      tx.amount,
      `"${getRunningBalance(tx)}"`,
      `"${tx.reference}"`,
      `"${tx.status}"`
    ]);

    // Create CSV string
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${activeReport}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'NGN',
    }).format(amount);
  };

  return (
    <div className="reports-page">
      <div className="reports-navigation">
        <button
          className={`nav-tab ${activeReport === 'all' ? 'active' : ''}`}
          onClick={() => handleReportTypeChange('all')}
        >
          <List size={20} />
          <span>All Transactions</span>
        </button>
        <button
          className={`nav-tab ${activeReport === 'card-activity' ? 'active' : ''}`}
          onClick={() => handleReportTypeChange('card-activity')}
        >
          <CreditCard size={20} />
          <span>Card Activity</span>
        </button>
      </div>

      <div className="transaction-logs-section">
        <div className="section-header">
          <div className="section-title">
            <h2>{activeReport === 'all' ? 'Transaction Logs' : 'Card Activity Report'}</h2>
            {loading && <RefreshCw size={16} className="animate-spin text-gray-400 ml-2" />}
          </div>
          <div className="section-actions">
            <Button variant="secondary" className="refresh-btn" onClick={handleRefresh}>
              <RefreshCw size={16} />
              Refresh
            </Button>
            <Button variant="secondary" className="print-btn" onClick={handlePrint}>
              <Printer size={16} />
              Print
            </Button>
            <Button variant="secondary" className="csv-btn" onClick={handleExportCSV}>
              <FileDown size={16} />
              CSV
            </Button>
            <Button
              variant="primary"
              className="upload-btn"
              onClick={handleUploadClick}
              disabled={loading}
            >
              <Upload size={16} />
              Upload CSV
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv"
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <div className="table-controls">
          <div className="left-controls">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search this table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <Button variant="secondary" className="filter-btn">
              <Filter size={16} />
              <span>Filter</span>
            </Button>
          </div>
          <div className="right-controls">
            <div className="entries-control">
              <span>Show</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="entries-select"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
        </div>

        <div className="transactions-table">
          <table>
            <thead>
              <tr>
                <th>Company / User</th>
                <th>CID</th>
                <th>Type</th>
                <th>Datetime</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Running Balance</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="empty-table">No transactions found</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <div className="company-info">
                        <span className="company-name">{transaction.user?.company?.legalName || (transaction.user ? `${transaction.user.firstName} ${transaction.user.lastName}` : 'Individual')}</span>
                        {transaction.user?.email && (
                          <span className="user-email" style={{ fontSize: '12px', color: '#6b7280' }}>{transaction.user.email}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="cid-value" style={{ fontWeight: 600, color: '#ff6b35', fontFamily: 'monospace' }}>
                        {getTransactionCid(transaction)}
                      </span>
                    </td>
                    <td>
                      <span className={`type-badge ${transaction.type.toLowerCase()}`}>
                        {transaction.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className="datetime-value">{formatDate(transaction.createdAt)}</span>
                    </td>
                    <td>
                      <span className="transaction-type">{transaction.description || '—'}</span>
                    </td>
                    <td>
                      <span className="amount-value">{formatAmount(transaction.amount, transaction.currency)}</span>
                    </td>
                    <td>
                      <span className="balance-value" style={{ fontWeight: 600, color: '#047857' }}>
                        {getRunningBalance(transaction)}
                      </span>
                    </td>
                    <td>
                      <span className="reference-value">{transaction.reference}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${transaction.status.toLowerCase()}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td>
                      <div className="transaction-actions">
                        <TransactionActionDropdown
                          transaction={transaction}
                          onViewDetails={handleViewDetails}
                          onDownloadReceipt={handleDownloadReceipt}
                          onPrintReceipt={handlePrintReceipt}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="showing-info">
            Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={page === 1 || loading}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </button>
            <div className="page-numbers">
              <button className="pagination-btn active">{page}</button>
            </div>
            <button
              className="pagination-btn"
              disabled={page * limit >= total || loading}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload Results"
        size="md"
        footer={
          <Button variant="primary" onClick={() => setIsUploadModalOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="upload-results-content">
          <div className="summary-stats">
            <div className="stat-item success">
              <CheckCircle size={24} />
              <div className="stat-info">
                <span className="stat-label">Success</span>
                <span className="stat-value">{uploadResults?.success || 0}</span>
              </div>
            </div>
            <div className="stat-item skipped">
              <RefreshCw size={24} />
              <div className="stat-info">
                <span className="stat-label">Skipped</span>
                <span className="stat-value">{uploadResults?.skipped || 0}</span>
              </div>
            </div>
            <div className="stat-item failed">
              <AlertCircleIcon size={24} />
              <div className="stat-info">
                <span className="stat-label">Errors</span>
                <span className="stat-value">{uploadResults?.errors?.length || 0}</span>
              </div>
            </div>
          </div>

          {uploadResults?.errors && uploadResults.errors.length > 0 && (
            <div className="error-list-container">
              <h4>Errors ({uploadResults.errors.length})</h4>
              <ul className="error-list">
                {uploadResults.errors.map((error, index) => (
                  <li key={index} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>

      <TransactionDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        transaction={selectedTransactionForDetails}
      />
    </div>
  );
};

export default ReportsPage;
